<template>
  <component :is="component" />
  <ModalTwo v-model:open="isShowAnnounce" />
</template>

<script setup lang="ts">
import { getConsentsBySRDId } from "@/apis/register/get";
import { Consent } from "@/apis/register/get/type";
import Loading from "@/components/Loading.vue";
import NotFound from "@/components/NotFound.vue";
import ModalTwo from "@/components/modals/ModalTwo.vue";
import { registerMetaTitle } from "@/configs/other";
import { ROUTE_NAMES } from "@/router/constants";
import type { StepStore } from "@/stores/type";
import { useRegisterParams } from "@/utils/helper";
import { message } from "ant-design-vue";
import _ from "lodash";
import type { Component } from "vue";
import { computed, inject, onMounted, ref, watchEffect } from "vue";
import { useMeta } from "vue-meta";
import { useRoute } from "vue-router";

const VALID_FORM_TYPES = [
  "CCT",
  "EC",
  "NACC",
  "NBTC",
  "NHRC",
  "OM",
  "PMEC",
  "SAO",
] as const;
type ValidFormType = (typeof VALID_FORM_TYPES)[number];

const route = useRoute();
const stepStore = inject<StepStore>("stepStore");
const { formType, senateRecruitDetailId, candidateInfoId } =
  useRegisterParams();

const component = ref<Component>(Loading);
const isShowAnnounce = ref(false);

const { meta } = useMeta({
  title: "สมัครเข้ารับการสรรหา",
  description: "รับสมัครบุคคล",
});

const normalizedFormType = computed(() => formType?.toUpperCase().trim() || "");

const isValidFormType = (type: string): type is ValidFormType => {
  return VALID_FORM_TYPES.includes(type as ValidFormType);
};

const loadFormComponent = async () => {
  if (!isValidFormType(normalizedFormType.value)) {
    component.value = NotFound;
    return;
  }

  try {
    const module = await import(
      `@/components/forms/registers/${normalizedFormType.value}.vue`
    );
    component.value = module?.default || NotFound;
  } catch (error) {
    console.error("Component load failed:", error);
    component.value = NotFound;
  }
};

const updateMeta = () => {
  if (isValidFormType(normalizedFormType.value)) {
    meta.title = registerMetaTitle[normalizedFormType.value];
    meta.description = `รับสมัครบุคคล - ${registerMetaTitle[normalizedFormType.value]}`;
  }
};

const setupForm = async () => {
  if (
    route.name === ROUTE_NAMES.NEW_REGISTER ||
    !normalizedFormType.value ||
    !senateRecruitDetailId ||
    !candidateInfoId
  ) {
    return;
  }

  try {
    const { status, data } = await getConsentsBySRDId(senateRecruitDetailId);
    const item = _.find(
      data?.active || [],
      (consent: Consent) => consent.id === Number(candidateInfoId),
    );

    if (status === 200 && item) {
      stepStore?.setState({
        status: item?.status ?? "D",
        step: item?.lastStep ?? 0,
        isCancel: item?.isCancel ?? "N",
      });
    } else {
      message.error("ระบบขัดข้อง");
    }
  } catch (error) {
    console.error("Failed to load consents:", error);
    return { name: ROUTE_NAMES.HOME };
  }
};

watchEffect(updateMeta);

onMounted(() => {
  loadFormComponent();
  setupForm();
});
</script>
